import {
  FinishTradeDataType,
  NonNullableOrderData,
  NonNullableSavingsPlanData,
  NonNullableStakeTransactionData,
  NonNullableTransactionData,
} from './reusableTypes';
import { TFunction } from 'i18next';
import {
  ActiveTransactionStatus,
  getTradeMessages,
  TradeMessage,
} from './getTradeMessages';
import {
  getSavingsPlanMessages,
  SavingsPlanMessage,
} from './getSavingsPlanMessages';
import { getStakeMessages, StakeMessage } from './getStakeMessages';
import {
  SavingsPlanStatus,
  StakingTransactionStatus,
} from '@bts-web/data-layer/server';
import {
  getOrderContentMessages,
  OrderDataWithValidType,
  OrderMessage,
} from './getOrderMessage';
import { OrderOperation } from '../../orders/types';

function isTransactionData(
  data: FinishTradeDataType,
): data is NonNullableTransactionData {
  return (
    data?.__typename === 'BuyTransaction' ||
    data?.__typename === 'SellTransaction' ||
    data.__typename === 'SwapTransaction' ||
    data.__typename === 'SwapOffer'
  );
}

function isSavingsPlanData(
  data: FinishTradeDataType,
): data is NonNullableSavingsPlanData {
  return data.__typename === 'SavingsPlan';
}

function isStakeData(
  data: FinishTradeDataType,
): data is NonNullableStakeTransactionData {
  return (
    data.__typename === 'StakeStakingTransaction' ||
    data.__typename === 'UnstakeStakingTransaction'
  );
}

function hasOrderTypename(
  data: FinishTradeDataType,
): data is NonNullableOrderData {
  return data.__typename === 'Order';
}

export const getTradeContentByTypeName = ({
  translationUtil,
  locale,
  transactionData,
  hasTaxOfflineWarning,
  reason,
}: {
  translationUtil: TFunction;
  locale: string;
  transactionData: FinishTradeDataType;
  hasTaxOfflineWarning: boolean;
  reason?: string;
}): TradeMessage | SavingsPlanMessage | StakeMessage | OrderMessage | null => {
  if (isTransactionData(transactionData)) {
    const { status } = transactionData ?? {
      status: 'IN_PROGRESS',
    };

    const tradeContentMessages = getTradeMessages({
      locale,
      translationUtil,
      transactionData: transactionData,
      hasTaxOfflineWarning,
    });

    return tradeContentMessages[status as ActiveTransactionStatus];
  } else if (isSavingsPlanData(transactionData)) {
    const { status } = transactionData ?? {
      status: 'ACTIVE',
    };

    const savingsPlanContentMessages = getSavingsPlanMessages({
      locale,
      translationUtil,
      transactionData: transactionData,
    });

    return savingsPlanContentMessages[status as SavingsPlanStatus];
  } else if (isStakeData(transactionData)) {
    const { status } = transactionData ?? {
      status: 'PENDING',
    };

    const stackContentMessages = getStakeMessages({
      locale,
      translationUtil,
      transactionData: transactionData,
      isUnstaking: transactionData.__typename === 'UnstakeStakingTransaction',
    });

    return stackContentMessages[status as StakingTransactionStatus];
  } else if (hasOrderTypename(transactionData)) {
    const { status, operation } = transactionData;

    if (operation !== OrderOperation.BUY && operation !== OrderOperation.SELL) {
      throw new Error(
        `Order type must be ${OrderOperation.BUY} or ${OrderOperation.SELL} for order content; received: ${String(operation)}`,
      );
    }

    const orderDataWithValidType: OrderDataWithValidType = {
      ...transactionData,
      operation: operation as OrderOperation,
    };

    const orderContentMessages = getOrderContentMessages({
      locale,
      translationUtil,
      transactionData: orderDataWithValidType,
      reason,
    });

    return orderContentMessages[status];
  } else {
    return null;
  }
};
